import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import withRouter from "../../components/Common/withRouter"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import * as Yup from "yup"
import { useFormik } from "formik"
import { loginUser } from "../../store/actions"
import { get } from "../../helpers/api_helper"

/**
 * Login — Calyvern two-panel layout (brand + bento module grid left, sign-in card
 * right). Brand (logo / name / subtitle) is dynamic from Application Settings via
 * the public /branding endpoint, defaulting to iTrobes. Auth wiring (Formik +
 * loginUser) is unchanged.
 */

const ACCENT = "#0c36a5"
const IMG_BASE = process.env.REACT_APP_IMG_APIKEY
const ITROBES_LOGO = `${process.env.PUBLIC_URL}/brand/itrobes-logo.svg`

// Quick-fill demo accounts (local/dev convenience).
const DEMO_LOGINS = [
  { label: "Admin", email: "superadmin@gmail.com", password: "Admin@12345" },
  { label: "Developer", email: "developer@itrobes.com", password: "Dev@12345" },
]

const tile = {
  display: "flex", alignItems: "center", gap: 12, padding: "0 16px", borderRadius: 14,
  background: "#fff", border: "1px solid #e6e9f1", boxShadow: "0 1px 2px rgba(20,30,60,0.05)", color: "#14171c",
}
const iconBox = {
  width: 34, height: 34, flex: "none", borderRadius: 9, background: "rgba(47,87,196,0.10)",
  display: "flex", alignItems: "center", justifyContent: "center", color: "#2f57c4",
}
const tileLabel = { fontSize: 14.5, fontWeight: 500, letterSpacing: "-0.01em" }

const MODULES = [
  { n: "Procurement", d: "M2 3h2l2.3 12.2a1 1 0 0 0 1 .8h8.8a1 1 0 0 0 1-.8L20 7H5.2", extra: <><circle cx="9" cy="20" r="1.3" /><circle cx="18" cy="20" r="1.3" /></> },
  { n: "Inventory", d: "M3 7l9-4 9 4-9 4-9-4Z M3 7v10l9 4 9-4V7 M12 11v10" },
  { n: "HRMS", d: "M3 20c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4 M16 5a3 3 0 0 1 0 6 M18.6 20c0-2.5-1-4.2-2.5-5.1", extra: <circle cx="9" cy="8" r="3.1" /> },
  { n: "Project Management", rects: true },
  { n: "Fleet Management", d: "M3 6h11v9H3z M14 9h4l3 3v3h-7z", extra: <><circle cx="7" cy="18.5" r="1.5" /><circle cx="17.5" cy="18.5" r="1.5" /></> },
  { n: "Camp Management", d: "M12 4 4 20h16L12 4Z M9 20l3-6 3 6" },
]

const Login = props => {
  const dispatch = useDispatch()
  const [passwordShown, setPasswordShown] = useState(false)
  const [brand, setBrand] = useState({ name: "", subtitle: "", logo: null })

  const displayName = brand.name || "iTrobes"
  document.title = `Sign in · ${displayName}`

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const r = await get("branding")
        if (alive && r) setBrand({ name: (r.name || "").trim(), subtitle: (r.subtitle || "").trim(), logo: r.logo || null })
      } catch (e) { /* keep iTrobes default */ }
    })()
    document.body.className = ""
    document.body.style.background = "#fff"
    return () => { alive = false; document.body.style.background = "" }
  }, [])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().required("Please enter your email"),
      password: Yup.string().required("Please enter your password"),
    }),
    onSubmit: values => dispatch(loginUser(values, props.router.navigate)),
  })

  const { error, loading } = useSelector(
    createSelector(s => s.Login, l => ({ error: l.error, loading: l.loading }))
  )

  // Lockup precedence: uploaded logo > set name (text) > iTrobes default logo.
  const hasLogo = !!brand.logo
  const hasName = !!brand.name

  return (
    <React.Fragment>
      <style>{`
        @keyframes tileGlowL {
          0%{border-color:#e6e9f1;background-color:#fff;box-shadow:0 1px 2px rgba(20,30,60,.05);transform:translateY(0)}
          4%,13%{border-color:#2f57c4;background-color:#f3f7ff;box-shadow:0 14px 30px rgba(47,87,196,.18);transform:translateY(-3px)}
          18%,100%{border-color:#e6e9f1;background-color:#fff;box-shadow:0 1px 2px rgba(20,30,60,.05);transform:translateY(0)}
        }
        @keyframes dotPulse{0%,100%{opacity:.4}50%{opacity:1}}
        .cly-in{transition:border-color .16s ease, box-shadow .16s ease}
        .cly-in::placeholder{color:#9aa3b2}
        .cly-in:focus{border-color:${ACCENT}!important;box-shadow:0 0 0 3px rgba(12,54,165,.15)!important}
        .cly-cta{transition:transform .12s ease, box-shadow .2s ease}
        .cly-cta:hover{transform:translateY(-1px);box-shadow:0 14px 32px rgba(12,40,90,.28)}
        .cly-cta:active{transform:translateY(0)}
        .cly-cta:disabled{opacity:.7;cursor:default;transform:none}
        .cly-demo{transition:background .12s ease, border-color .12s ease}
        .cly-demo:hover{background:#f1f4fb;border-color:#c9d3ea}
        @media (max-width: 980px){ .cly-left{display:none!important} .cly-right{flex:1 1 100%!important;border-left:none!important} }
      `}</style>

      <div style={{ height: "100vh", overflow: "hidden", background: "#fff", fontFamily: "Sora, sans-serif", display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 1240, display: "flex", alignItems: "stretch" }}>
        {/* LEFT — brand + bento */}
        <div className="cly-left" style={{ flex: "1 1 0", minWidth: 0, position: "relative", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "0 48px", overflow: "hidden", background: "#fff" }}>
          <div style={{ position: "relative", maxWidth: 540 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 26 }}>
              {hasLogo ? (
                <>
                  <img src={`${IMG_BASE}${brand.logo}`} alt={displayName} style={{ height: 46, maxWidth: 120, objectFit: "contain" }} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {hasName ? <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: "#14171c", lineHeight: 1 }}>{brand.name}</span> : null}
                  </div>
                </>
              ) : hasName ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: "#14171c", lineHeight: 1 }}>{brand.name}</span>
                </div>
              ) : (
                <img src={ITROBES_LOGO} alt="iTrobes" style={{ height: 44, maxWidth: 200, objectFit: "contain" }} />
              )}
            </div>

            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 13px", borderRadius: 999, background: "#f1f4fb", border: "1px solid #e3e8f3", marginBottom: 16 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1f9d6b", boxShadow: "0 0 9px rgba(31,157,107,0.7)", animation: "dotPulse 2.6s ease-in-out infinite" }} />
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#56657c", textTransform: "uppercase" }}>{brand.subtitle || "Enterprise Business OS"}</span>
            </div>
            <h2 style={{ fontSize: 29, fontWeight: 700, letterSpacing: "-0.025em", color: "#14171c", lineHeight: 1.16, margin: "0 0 24px", maxWidth: 440 }}>The operating system for your entire group.</h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gridAutoRows: "74px", gap: 11, maxWidth: 492 }}>
              <div data-tile style={{ ...tile, gridColumn: "span 2", gridRow: "span 2", flexDirection: "column", justifyContent: "space-between", alignItems: "stretch", padding: 16, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ ...iconBox, width: 38, height: 38, borderRadius: 10 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20" /><path d="M17 6.5C17 4.8 14.8 4 12 4S7 4.8 7 6.5 9.2 9 12 9.5s5 1.3 5 3-2.2 2.5-5 2.5-5-.8-5-2.5" /></svg>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", color: "#aeb6c4", textTransform: "uppercase" }}>Core</span>
                </div>
                <div>
                  <svg width="100%" height="18" viewBox="0 0 200 34" preserveAspectRatio="none" style={{ display: "block", marginBottom: 4 }}><polyline points="0,26 28,22 52,25 78,14 104,18 130,9 156,13 182,5 200,8" fill="none" stroke="#2f57c4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.01em" }}>Finance</div>
                  <div style={{ fontSize: 11.5, color: "#6a7488", marginTop: 2, whiteSpace: "nowrap" }}>Real-time books, every entity</div>
                </div>
              </div>
              {MODULES.map(m => (
                <div data-tile key={m.n} style={{ ...tile, gridColumn: "span 2" }}>
                  <div style={iconBox}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {m.rects ? (<><rect x="3" y="4" width="5" height="16" rx="1" /><rect x="10" y="4" width="5" height="10" rx="1" /><rect x="17" y="4" width="4" height="13" rx="1" /></>) : (<>{m.extra}{m.d && m.d.split(" M").map((seg, k) => <path key={k} d={(k ? "M" : "") + seg} />)}</>)}
                    </svg>
                  </div>
                  <span style={tileLabel}>{m.n}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 22 }}>
              <span style={{ fontSize: 12, color: "#8a93a3" }}>© 2026 {displayName} · An iTrobes product</span>
            </div>
          </div>
        </div>

        {/* RIGHT — login card */}
        <div className="cly-right" style={{ flex: "0 0 600px", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 56px", borderLeft: "1px solid #eef0f5", background: "#fff" }}>
          <div style={{ width: "100%", maxWidth: 410, padding: "34px 36px 30px", borderRadius: 22, background: "#fff", border: "1px solid #ececf2", boxShadow: "0 24px 60px rgba(20,30,70,0.10)" }}>
            <h1 style={{ fontSize: 25, fontWeight: 700, letterSpacing: "-0.02em", color: "#14171c", margin: "0 0 8px" }}>Welcome back</h1>
            <p style={{ fontSize: 14, lineHeight: 1.5, color: "#5a6675", margin: "0 0 24px" }}>Sign in to your {displayName} workspace.</p>

            {error ? (
              <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: "#fdecec", border: "1px solid #f5c2c2", color: "#b42318", fontSize: 13 }}>{error}</div>
            ) : null}

            <form onSubmit={e => { e.preventDefault(); validation.handleSubmit(); return false }} autoComplete="on">
              <label style={{ display: "block", fontSize: 12.5, fontWeight: 500, color: "#3c4757", marginBottom: 7 }}>Work email</label>
              <input
                className="cly-in" type="email" name="email" placeholder="you@yourcompany.com"
                value={validation.values.email} onChange={validation.handleChange} onBlur={validation.handleBlur}
                style={{ width: "100%", height: 46, padding: "0 15px", fontFamily: "Sora, sans-serif", fontSize: 14, color: "#14171c", background: "#fff", border: `1px solid ${validation.touched.email && validation.errors.email ? "#e57373" : "#dfe3ec"}`, borderRadius: 11, outline: "none", marginBottom: validation.touched.email && validation.errors.email ? 6 : 16 }}
              />
              {validation.touched.email && validation.errors.email ? (
                <div style={{ color: "#b42318", fontSize: 12, marginBottom: 12 }}>{validation.errors.email}</div>
              ) : null}

              <label style={{ display: "block", fontSize: 12.5, fontWeight: 500, color: "#3c4757", marginBottom: 7 }}>Password</label>
              <div style={{ position: "relative", marginBottom: validation.touched.password && validation.errors.password ? 6 : 18 }}>
                <input
                  className="cly-in" type={passwordShown ? "text" : "password"} name="password" placeholder="Enter your password"
                  value={validation.values.password} onChange={validation.handleChange} onBlur={validation.handleBlur}
                  style={{ width: "100%", height: 46, padding: "0 44px 0 15px", fontFamily: "Sora, sans-serif", fontSize: 14, color: "#14171c", background: "#fff", border: `1px solid ${validation.touched.password && validation.errors.password ? "#e57373" : "#dfe3ec"}`, borderRadius: 11, outline: "none" }}
                />
                <button type="button" aria-label="Show password" onClick={() => setPasswordShown(s => !s)}
                  style={{ position: "absolute", right: 6, top: 5, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", cursor: "pointer", color: "#8a93a3" }}>
                  {passwordShown ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 7 10 7a9.1 9.1 0 0 0 5.39-1.61" /><line x1="2" y1="2" x2="22" y2="22" /></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
              {validation.touched.password && validation.errors.password ? (
                <div style={{ color: "#b42318", fontSize: 12, marginBottom: 14 }}>{validation.errors.password}</div>
              ) : null}

              <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", marginBottom: 18, userSelect: "none" }}>
                <input type="checkbox" name="remember" style={{ width: 16, height: 16, accentColor: ACCENT, cursor: "pointer" }} />
                <span style={{ fontSize: 13, color: "#4a5666" }}>Keep me signed in</span>
              </label>

              <button type="submit" className="cly-cta" disabled={loading}
                style={{ width: "100%", height: 48, fontFamily: "Sora, sans-serif", fontSize: 15, fontWeight: 600, color: "#fff", background: "linear-gradient(135deg,#2f57c4,#0c36a5)", border: "none", borderRadius: 11, cursor: "pointer", letterSpacing: "0.01em", boxShadow: "0 10px 24px rgba(12,40,90,0.24)" }}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            {/* Demo logins — quick fill */}
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #eef0f5" }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em", color: "#8a93a3", textTransform: "uppercase", marginBottom: 9 }}>Demo logins</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {DEMO_LOGINS.map(d => (
                  <button key={d.email} type="button" className="cly-demo"
                    title={`${d.email} / ${d.password}`}
                    onClick={() => validation.setValues({ email: d.email, password: d.password })}
                    style={{ fontFamily: "Sora, sans-serif", fontSize: 12.5, fontWeight: 500, color: ACCENT, background: "#f7f9fd", border: "1px solid #e1e8f5", borderRadius: 9, padding: "7px 13px", cursor: "pointer" }}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  router: PropTypes.object,
}
